<template>
  <div>
    <div class="relative w-full max-w-lg mx-auto">

      <!-- Cart button top right -->
      <div class="cart-container">
        <button class="cart-btn" @click="openCartModal">
          <div class="relative">
            <img
              :src="require('@/assets/cart.svg')"
              alt="Cart"
              class="cart-icon"
            />
            <span class="absolute -bottom-2 -right-2 cart-counter">
              {{ selectedImages.length }}
            </span>
          </div>
        </button>
      </div>

      <!-- Header -->
      <div class="text-center mt-4 mx-auto max-w-7xl px-4 sm:mt-2 sm:px-6">
        <h2 class="text-2xl tracking-tight font-extrabold text-gray-900">
          {{ name }}
        </h2>
        <h2 class="text-xl font-bold text-gray-900 whitespace-nowrap">
          🎓 Your moment, your memory. 🎓
        </h2>
        <p class="mt-2 text-gray-700">
          Choose the Snapshot(s) that brings your achievement to life.
        </p>
        <p class="mt-2 text-gray-700">
          You can choose as many as you like!
        </p>
        <p v-if="isFreeDigital" class="mt-2 text-gray-700 font-semibold">
          Digital snapshots for this ceremony are free — add your favourites and download them instantly.
        </p>
      </div>

      <div
        v-if="freeDownloadSuccess"
        class="mt-4 mx-auto max-w-3xl bg-green-100 text-green-800 p-4 rounded-lg"
      >
        <p>
          Your digital images are downloading. If it doesn't start automatically,
          <button
            class="font-semibold underline"
            type="button"
            @click="retryDownload"
            :disabled="freeDownloadInProgress"
          >
            click here to try again
          </button>.
        </p>
        <p v-if="lastOrderNanoId" class="mt-2">
          Collection code: <span class="font-semibold">{{ lastOrderNanoId }}</span>
        </p>
      </div>
      <div
        v-else-if="freeDownloadError"
        class="mt-4 mx-auto max-w-3xl bg-red-100 text-red-800 p-4 rounded-lg"
      >
        <p>{{ freeDownloadError }}</p>
        <button
          class="mt-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
          type="button"
          @click="retryDownload"
          :disabled="freeDownloadInProgress"
        >
          Try again
        </button>
      </div>
      <div
        v-else-if="freeDownloadInProgress"
        class="mt-4 mx-auto max-w-3xl bg-blue-100 text-blue-800 p-4 rounded-lg"
      >
        <p>We're preparing your download. This can take a few moments.</p>
      </div>

      <!-- Grid/Carousel View -->
      <div v-if="!showCarousel">
        <preview-grid
          :images="images"
          :selectedImages="selectedImages"
          @select-image="showCarouselAtIndex"
        />
      </div>
      <div v-else>
        <preview-carousel
          :images="images"
          :selectedImages="selectedImages"
          :settings="carouselSettings"
          :initial-slide="currentImageIndex"
          @add-to-cart="handleAddToCart"
          @back-to-grid="showCarousel = false"
          @view-cart="openCartModal"
        />
      </div>
    </div>

    <!-- Cart Modal -->
    <cart-modal
      :is-open="isCartModalOpen"
      :items="selectedImages"
      :basketUrl="basketUrl"
      :is-free-digital="isFreeDigital"
      @remove="removeItem"
      @close="closeCartModal"
      @submit="goToCheckout"
      @share="shareBasket"
    />
    <div v-if="!isUCD" class="text-center mt-4 mx-auto max-w-lg px-4 sm:mt-2 sm:px-6">
        <p class="text-xl font-bold text-gray-900">
          Note: Award Photo Prints are only available in person on the day of your ceremony. 
          </p><p>Please look for signage of where to go to pay and collect.
          You must select your images here prior to printing.
        </p>
       
      </div>

  </div>
</template>

<script>
import { trackEvent } from "../../Analytics.vue";
import PreviewCarousel from "./PreviewCarousel.vue";
import CartModal from "./CartModal.vue";
import PreviewGrid from "./PreviewGrid.vue";

export default {
  name: "SnapshotImageSelector",
  components: {
    PreviewCarousel,
    CartModal,
    PreviewGrid,
  },
  props: {
    name: String,
    images: Array,
    basketUrl: String,
    isFreeDigital: { type: Boolean, default: false },
    freeDownloadSuccess: { type: Boolean, default: false },
    freeDownloadError: { type: String, default: null },
    freeDownloadInProgress: { type: Boolean, default: false },
    lastOrderNanoId: { type: String, default: "" },
  },
  data() {
    return {
      selectedImages: [],
      isCartModalOpen: false,
      carouselSettings: {
        focusOnSelect: true,
        infinite: true,
        speed: 50,
        waitForAnimate: false,
        centerMode: true,
        lazyLoad: 'ondemand',
        slidesToShow: 3,
        slidesToScroll: 1,
        swipeToSlide: true,
        swipe: true,
        draggable: true,
        dots: false,
        arrows: true,
        responsive: [
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 1,
              infinite: true,
              dots: false,
            },
          },
          {
            breakpoint: 600,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 480,
            settings: {
              focusOnSelect: true,
              infinite: true,
              centerMode: true,
              slidesToShow: 1,
              slidesToScroll: 1,
              swipe: true,
              draggable: true,
              dots: false,
              arrows: true,

            },
          },
        ],
      },
      showCarousel: false,
      currentImageIndex: 0,
    };
  },
  created: function () {
    if(this.$route.query.shareBasket){
      this.images.forEach((item, i) => {
        this.handleAddToCart(i)
      });
      this.isCartModalOpen = true
    }
  },
  methods: {
    handleAddToCart(imageIndex) {
      // Grab the image from 'images' array by index
      const image = this.images[imageIndex];
      var printOnly = false;
      if (typeof this.$route.query.printOnly === undefined) {
        printOnly = false;
      }
      else {
        if (this.$route.query.printOnly == "true") {
          printOnly = true
        }
        else {
          printOnly = false
        }
      }
      // If it’s not already in selectedImages, push it
      if (!this.selectedImages.includes(image)) {
        // Initialize isDigitalSelected, isPrintSelected, etc.
        if (printOnly){
          this.$set(image, "isPrintSelected", true);
          this.$set(image, "isDigitalSelected", false);
          this.$set(image, "printQuantity", 1);

        }
        else {
          // Only allow digital
          this.$set(image, "isDigitalSelected", true);
          this.$set(image, "isPrintSelected", false);
        }
       
        this.selectedImages.push(image);
        trackEvent(this.$route.path, {
              label: "SNAPSHOT_CART_ADD_ITEM",
              img_index: imageIndex,
              type: image.isPrintSelected ? "PRINT" : "DIGITAL",
              img_src: image.src
        });
      } else {
        // Or remove if you'd like toggling behavior
        // this.selectedImages.splice(this.selectedImages.indexOf(image), 1);
      }
    },
    goToCheckout(items, form) {
      console.log("Going to checkout")
      this.isCartModalOpen = false;
      this.$emit("checkout", items, form)
    },
    retryDownload() {
      this.$emit('retry-free-download');
    },
    resetSelection() {
      this.selectedImages = [];
      this.closeCartModal();
    },
    shareBasket(items) {
      this.$emit('share', items)
    },
    openCartModal() {
      this.isCartModalOpen = true;
    },
    closeCartModal() {
      this.isCartModalOpen = false;
    },
    removeItem(image) {
      const idx = this.selectedImages.indexOf(image);
      if (idx !== -1) {
        this.selectedImages.splice(idx, 1);
      }
      if (this.selectedImages.length === 0) {
        this.closeCartModal();
      }
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_CART_REMOVE_ITEM",
        img_index: this.images.indexOf(image),
        img_src: image.src
      });
    },
    showCarouselAtIndex(index) {
      console.log("SHowing Carousel")
      console.log(index)
      this.currentImageIndex = index;
      this.showCarousel = true;
      trackEvent(this.$route.path, {
        label: "SNAPSHOT_DISPLAY_CAROUSEL",
        img_index: index
      });
      
    },
  },
  computed: {
    isUCD() {
      const slug = this.$route.params.uni || '';
      return slug === 'ucd' || slug === 'ucd-test';
    }
  }
};
</script>

<style scoped>


.cart-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.cart-btn {
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  padding: 8px;
}

.cart-icon {
  width: 3em;
  height: 3em;
}

.cart-counter {
  position: absolute;
  bottom: -8px;
  right: -8px;
  background-color: #08697b;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
  min-width: 20px;
  text-align: center;
}

/* Remove the old selected-images styles */
.selected-images {
  display: none;
}


</style>
